import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { IMAGE_SOURCE_PLACEHOLDER } from 'commons/containers/MediaLibrary';
import Node from 'craft/components/Node';
import { ImageCrop } from 'craft/components/SelectedToolbar/ImageCrop';
import { MediaLibraryButton } from 'craft/components/SelectedToolbar/MediaLibraryButton';
import { MediaSourceButton } from 'craft/components/SelectedToolbar/MediaSourceButton';
import { BuiltInComponents } from 'craft/constants';
import { parseVariables, stringWithFallbacks } from 'craft/containers/Actions';
import {
  useNodeContext,
  usePropsWithCollection,
  useResponsiveProps,
  useSelection,
} from 'craft/hooks';
import { ImageFromCollectionPlaceholder } from 'craft/resolvers/Image/ImageFromCollectionPlaceholder';
import { ImagePlaceholder } from 'craft/resolvers/Image/ImagePlaceholder';
import { setToolboxTab } from 'craft/state';
import { Resolver, ToolboxComponentSections, ToolboxTabs } from 'craft/types';

import { ImageToolbox } from './ImageToolbox';

export const CLASS_NAME = 'editor-image-container';

export const Image: Resolver = ({ src, cropLeft, cropTop, cropRight, cropBottom }: any) => {
  const { id } = useNodeContext();
  const {
    responsiveProps: { height, width },
  } = useResponsiveProps([id]);
  const { selection } = useSelection();
  const dispatch = useDispatch();
  // We need to know if node is about to inject an empty src
  const propsWithSource = usePropsWithCollection(id);

  useEffect(() => {
    if (selection.all.length !== 1 || selection.all[0] !== id) {
      return;
    }
    if (propsWithSource.src || src) {
      return;
    }
    dispatch(setToolboxTab(ToolboxTabs.Component));
  }, [selection.all, propsWithSource, src]);

  if (
    src &&
    (typeof cropTop !== 'undefined' ||
      typeof cropRight !== 'undefined' ||
      typeof cropBottom !== 'undefined' ||
      typeof cropLeft !== 'undefined')
  ) {
    // eslint-disable-next-line no-param-reassign
    src = `https://cdn.candu.ai/cdn-cgi/image/trim=${cropTop || 0};${cropRight || 0};${
      cropBottom || 0
    };${cropLeft || 0}/${src}`;
  }

  if (src === IMAGE_SOURCE_PLACEHOLDER && !propsWithSource.src) {
    return (
      <Node>
        <ImageFromCollectionPlaceholder />
      </Node>
    );
  }

  if (!src) {
    return (
      <Node>
        <ImagePlaceholder />
      </Node>
    );
  }

  const variables = parseVariables(src);

  if (variables.length === 0) {
    return (
      <Node
        is="img"
        className={`${CLASS_NAME} candu-img`}
        style={{ objectPosition: '0 0', height, width }}
        alt=""
        src={src}
      />
    );
  }

  // we can preview if all variables have a fallback
  if (variables.find((v) => v.fallback === undefined) === undefined) {
    return (
      <Node
        is="img"
        className={`${CLASS_NAME} candu-img`}
        style={{ objectPosition: '0 0', height, width }}
        alt=""
        src={stringWithFallbacks(src)}
      />
    );
  }

  // show a placeholder if we have variables without fallbacks
  return (
    <Node>
      <ImagePlaceholder />
    </Node>
  );
};

Image.craft = {
  name: BuiltInComponents.Image,
  related: {},
};

Image.toolbox = {
  component: [ImageToolbox],
  schema: {
    [ToolboxComponentSections.Layout]: {
      blockedDisplays: ['flex', 'inline-block'],
    },
    [ToolboxComponentSections.Size]: {
      hasObjectFit: true,
      hasAutoSize: true,
    },
  },
};

Image.selectedToolbar = {
  customItems: [MediaSourceButton, ImageCrop, MediaLibraryButton],
};
